import {
    MESSAGE_KEY_INSTANCE_ID,
    MESSAGE_KEY_FORM_SIZE,
    MESSAGE_KEY_REDIRECT,
    MESSAGE_KEY_DO_RENDER_CAPTCHA,
    MESSAGE_KEY_DO_TAKE_CAPTCHA_TOKEN,
    MESSAGE_KEY_RESPONSE_STATUS,
    MESSAGE_DATA_OBJECT_KEY,
    MESSAGE_KEY_EVENT_CODE,
    MESSAGE_KEY_ARGUMENT,
    MESSAGE_KEY_EVENT_FRAME_KEY,
    MESSAGE_KEY_ORIGIN,
    MESSAGE_KEY_DO_FORWARD,
} from "../const/Constant.js";
import { APP_ORIGIN } from "../../ENV.js";
import Logger from "../utils/Logger.js";
import { observable } from "./observable.js";
import { isIframe, queryFrameKey } from "../common/Common.js";


/** 
 *  @callback fnSubcribeMessage
 *  @param {string} key
 *  @param {(value) =>{}} onMessageIncoming
 * @returns {Function | void}
 * 
*/

/** 
 *  @callback fnDoPostMessage
 *  @param {string} key
 *  @param {any} value
 *  @param {ArrayBuffer | any} transfers
 * 
*/

/** 
 *  @callback fnSetWindowDoPostMessage
 *  @param {Window} window
 * 
*/

/** 
 * 
 *  @typedef {{add: (cb:function, injectKeys: Array<string>) => function }} MiddlewareMessage
 * 
 * 
*/


/**
 * @type {MessageService}
 */
const {
    subscribeMessage,
    subscribeMessageOnce,
    doPostMessage,
    setWindowDoPostMessage,
    middlewareMessageIncomming,
    middlewareDoSendMessage,
    registerForwardMessageByKey,
    setRecipientOrigin,
    setFrameKey
} = (function (window) {
    if (window['__TaiMessageService']) {
        return window['__TaiMessageService'].call(null);
    }

    const getName = () => isIframe() ? 'iframe' : 'parent';

    const logWhoRecieveMessage = (key = '', value = '') => {
        // Logger.info("Receiver message:", window !== window.parent ? 'iframe' : 'parent', key, value);
    }
    /**
     * 
     * @param {any} defaultValue 
     * @param {string} uniqueKey 
     * @returns 
     */
    const useGetSet = (() => {
        if (!window['__TaiFormCaching']) {
            window['__TaiFormCaching'] = new Map();
        }
        const caching = new Map();

        return (defaultValue = '', uniqueKey = Symbol('unique')) => {
            let value = defaultValue;
            caching.set(uniqueKey, defaultValue);
            const fn = {
                getter() {
                    // return caching.get(uniqueKey)
                    return value;
                },
                setter(val) {
                    value = val;
                    // caching.set(uniqueKey, val);
                }
            }
            return [fn.getter, fn.setter];
        }
    }).call(null)

    const _instanceId = Date.now().toString(36) + Math.random().toString(36).substring(2);

    const [getPostWindow, setWindowDoPostMessage] = useGetSet(window.parent, 'windowDoPostMessage');

    const [subscribeMessage, emitIncomingMessage] = observable();

    const [getFrameKey, setFrameKey] = useGetSet(queryFrameKey(), 'frameKey');

    const [getRecipientOrigin, setRecipientOrigin] = (() => {
        const toOrigin = (domain) => {
            if (domain.length > 0 && (domain.indexOf('https://') == -1 && domain.indexOf('http://') == -1)) {
                domain = 'https://' + domain;
            }
            return domain;
        }
        const [getter, setter] = useGetSet(APP_ORIGIN, 'recipientOrigin');
        const set = (value) => {
            value = toOrigin(value);
            setter(value);
        }
        const get = () => {
            const value = window.top !== window.self ? getter() : APP_ORIGIN;
            return value;
        };
        return [get, set]
    }).call(null);

    const isSameWindow = () => {
        return getPostWindow() === window;
    };

    const subscribeMessageOnce = (key, cb = (val) => { }) => {
        const unsubscribe = subscribeMessage(key, (value) => {
            cb.call(null, value);
            unsubscribe();
        })

    }

    subscribeMessageOnce(MESSAGE_KEY_ORIGIN, (val) => {
        setRecipientOrigin(val);
    })

    const validateBeforeSendMessage = () => {
        return new Promise((next, reject) => {
            if (isSameWindow()) {
                reject('Same Window');
                return;
            }
            next(true);
        });
    }

    /**
     * 
     * @param {MessageEvent} event
     * @returns 
     */
    const validateBeforeRecieveMessage = (() => {

        const isNotOurMessage = data => !(!!data[MESSAGE_DATA_OBJECT_KEY]);

        const isMessageComeFromAllowedOrigin = origin => origin === getRecipientOrigin();

        const isNotMessageForMe = data => {
            const {
                [MESSAGE_KEY_EVENT_FRAME_KEY]: frameKey
            } = data[MESSAGE_DATA_OBJECT_KEY];
            return !isIframe() && frameKey !== getFrameKey();
        }

        return event => new Promise((next, reject) => {
            const { origin, data } = event;

            if (!isMessageComeFromAllowedOrigin(origin)) {
                reject('not process message ' + JSON.stringify(data));
                return;
            }
            if (isNotOurMessage(data)) {
                reject('not process message ' + JSON.stringify(data));
                return;
            }

            /*
             Only check from parent site (the site embed iframe).
            The iframe will get the frameKey by url parameter then embed this key to per send message  
             */
            if (isNotMessageForMe(data)) {
                reject('Not my message');
                return;
            }

            if (isSameWindow()) {
                reject('Same window');
                return;
            }
            next(event);
        });
    }).call(null)


    /**
     * 
     * @returns {[MiddlewareMessage, Function, Set<object>]}
     */
    const fnMiddleware = () => {
        const queue = new Set();

        const execute = (key, value) => new Promise(next => {
            queue.forEach(({ key: k, cb }) => {
                if (k === key) {
                    value = cb.call(null, key, value) || value;
                }
            })
            next(value);
        })



        /**
         * @type {MiddlewareMessage}
         */
        const handle = {
            add: (cb, injectKeys = []) => {
                if (!Array.isArray(injectKeys)) {
                    throw "injectKeys must be array";
                } else if (injectKeys.length === 0) {
                    throw "injectKeys is empty";
                }
                if (typeof cb !== 'function') {
                    Logger.error("cb must be a function");
                    return () => { };
                }
                const arr = [];
                injectKeys.forEach(key => {
                    const newObj = {
                        key,
                        cb
                    }
                    queue.add(newObj);
                    arr.push(newObj);
                })
                return () => {
                    arr.forEach(item => {
                        queue.delete(item);
                    })
                };
            }
        }
        return [handle, execute, queue];
    };

    const [middlewareMessageIncomming, emitMiddlewareMessageIn] = fnMiddleware();

    const [middlewareDoSendMessage, emitMiddlewareMessageOut] = fnMiddleware();


    const generateEventCode = (() => {
        let event_count = 0;
        return (key = '') => `mess_${new Date().getTime()}${++event_count}${key.length > 0 ? ('_' + key.toLowerCase()) : ''}`

    }).call(null);

    /**
     * @param {string} key,
     * @param {any} value
     */
    const doPostMessage = (() => {

        const isNeedReply = (key) => {
            return !!({
                [MESSAGE_KEY_FORM_SIZE]: false,
                [MESSAGE_KEY_REDIRECT]: false,
                [MESSAGE_KEY_DO_RENDER_CAPTCHA]: true,
                [MESSAGE_KEY_DO_TAKE_CAPTCHA_TOKEN]: true,
            }[key])
        }

        const waitingTargetOrigin = () => {
            return new Promise((next, reject) => {
                let countCheck = 5;
                const run = () => {
                    countCheck--;
                    if (getRecipientOrigin().length > 0) {
                        next(getRecipientOrigin());
                        clearInterval(itv)
                    } else if (countCheck === 0) {
                        reject('Origin invalid');
                        clearInterval(itv)
                    }
                }
                run();
                const itv = setInterval(run, 1000)
            });
        }

        const getMessageBody = (key, value) => {
            const payload = {
                [key]: value
            }
            // If eventCode is empty no need reply
            const eventCode = (isNeedReply(key)) ? generateEventCode(key) : '';
            const output = {
                [MESSAGE_KEY_ARGUMENT]: payload,
                [MESSAGE_KEY_INSTANCE_ID]: _instanceId,
                'provider': isIframe() ? 'form' : 'parent'
            }
            if (eventCode) {
                output[MESSAGE_KEY_EVENT_CODE] = eventCode;
            }

            if (isIframe()) {
                output[MESSAGE_KEY_EVENT_FRAME_KEY] = getFrameKey();

            }
            return output;
        }

        const sendMessage = (data, targetOrigin, transfers) => {
            // const targetWindow = getWindow();
            getPostWindow().postMessage({
                [MESSAGE_DATA_OBJECT_KEY]: data
            }, targetOrigin, transfers);
        }


        return async (key, value, transfers) => {
            let targetOrigin = '';
            
            try {
                const isValid = await validateBeforeSendMessage();
                if (!isValid) return;
                targetOrigin = await waitingTargetOrigin();
                const data = getMessageBody(key, value);
                const newestData = await emitMiddlewareMessageOut(key, data);
                sendMessage(newestData, targetOrigin, transfers);
                const eventCode = newestData[MESSAGE_KEY_EVENT_CODE];
                // if eventCode is falsy then no need recieve reply message
                if (!eventCode) return;
                subscribeMessageOnce(MESSAGE_KEY_RESPONSE_STATUS, (key) => {
                    if (key !== eventCode) { return }
                    Logger.success("[Message Replied] " + eventCode)
                })
            } catch (msg) {
                // Logger.unsuccess(`[${getName()}]`, msg)
                console.log({key,value})
                throw `${msg} `
            }
            // return validateBeforeSendMessage()
            //     .then(() => {
            //         return waitingTargetOrigin();
            //     }).then((origin) => {
            //         targetOrigin = origin;
            //         const data = getMessageBody(key, value);
            //         return emitMiddlewareMessageOut(key, data);
            //     }).then((newestData) => {
            //         sendMessage(newestData, targetOrigin, transfers);
            //         const eventCode = newestData[MESSAGE_KEY_EVENT_CODE];
            //         return Promise.resolve(eventCode);
            //     }).then((eventCode) => {
            //         if (!eventCode) {
            //             return;
            //         }
            //         subscribeMessageOnce(MESSAGE_KEY_RESPONSE_STATUS, (key) => {
            //             if (key !== eventCode) { return }
            //             Logger.success("[Message Replied] " + eventCode)
            //         })
            //     }).catch((msg) => {
            //         Logger.unsuccess(`[${getName()}]`, msg)
            //     })
        }
    })();

    /**
     * 
     * @param { MessageEvent } data 
     * @returns 
     */
    const onMessageIncoming = (() => {
        const extractOurMessage = evData => evData[MESSAGE_DATA_OBJECT_KEY];

        const doForwardMessage = (data) => {
            data[MESSAGE_KEY_DO_FORWARD] = 0;
            data.provider = 'form';
            data[MESSAGE_KEY_EVENT_FRAME_KEY] = getFrameKey();
            window.parent.postMessage({
                [MESSAGE_DATA_OBJECT_KEY]: data
            }, getRecipientOrigin())
        }

        const doReplyMessage = (eventCode, response) => {
            // If eventCode is not empty then reply message
            if (eventCode && !response) {
                doPostMessage(MESSAGE_KEY_RESPONSE_STATUS, eventCode);
            }
        }
        /**
         * 
         * @param { MessageEvent } messageEvent
         * @returns 
         */
        return (messageEvent) => {
            validateBeforeRecieveMessage(messageEvent)
                .then(event => {
                    const ourData = extractOurMessage(event.data)
                    return Promise.resolve(ourData);
                }).then(payload => {
                    const {
                        [MESSAGE_KEY_ARGUMENT]: args,
                        [MESSAGE_KEY_EVENT_FRAME_KEY]: frameKey,
                        [MESSAGE_KEY_EVENT_CODE]: eventCode,
                        [MESSAGE_KEY_DO_FORWARD]: needForward,
                        [MESSAGE_KEY_INSTANCE_ID]: fromInstanceId,
                        [MESSAGE_KEY_RESPONSE_STATUS]: response
                    } = payload;

                    if (needForward && fromInstanceId !== _instanceId) {
                        doForwardMessage(payload);
                        return;
                    }
                    for (const key in args) {
                        const value = args[key];
                        emitMiddlewareMessageIn(key, value).then(() => {
                            emitIncomingMessage(key, value);
                            return Promise.resolve()
                        }).then(() => {
                            doReplyMessage(eventCode, response);
                        });
                    }
                }).catch(er => {
                    // Logger.unsuccess(er)
                })

        }
    }).call(null);



    // Register the key that forward message immediately (iframe => parent) without do any handlers
    const registerForwardMessageByKey = (keys = []) => {
        if (window !== window.parent) return;
        if (!Array.isArray(keys)) {
            throw 'keys must an array';
        }
        middlewareDoSendMessage.add((key, payload) => {
            if (keys.includes(key)) {
                payload[MESSAGE_KEY_DO_FORWARD] = 1;
            }
            return payload;
        }, keys);
        return () => {

        }
    }

    window.addEventListener('message', e => {
        onMessageIncoming(e)
    })

    /**
     * 
     * @returns {MessageService}
     */
    const MessageService = () => ({
        subscribeMessage,
        subscribeMessageOnce,
        doPostMessage,
        setWindowDoPostMessage,
        middlewareMessageIncomming,
        middlewareDoSendMessage,
        registerForwardMessageByKey,
        setRecipientOrigin,
        setFrameKey
    });


    /** @type {MessageService} */
    window['__TaiMessageService'] = MessageService;
    return MessageService.call(null);
})(global.window);

export {
    subscribeMessage,
    subscribeMessageOnce,
    doPostMessage,
    setWindowDoPostMessage,
    middlewareMessageIncomming,
    middlewareDoSendMessage,
    registerForwardMessageByKey,
    setRecipientOrigin,
    setFrameKey
}   